/**
 * FauxDS FilterToggle - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate FilterToggle component for prototyping.
 * It is NOT the real @faire/slate FilterToggle and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate FilterToggle, see: @faire/slate/components/filter
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 48995:1701 (Slate Filter Toggle)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Applied (true/false)
 * - All states verified: Default, Hover, Active, Disabled
 * - Icon support verified (optional)
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef } from "react";

export interface FilterToggleProps {
  /**
   * The label text to display
   * @default "Label"
   */
  label?: string;
  /**
   * Custom icon component (ReactNode)
   * If not provided and icon is true, no icon is shown
   */
  Icon?: React.FC<any>;
  /**
   * Accessibility label for screen readers
   * @default label
   */
  "aria-label"?: string;
  /**
   * Whether the filter is applied (affects visual styling)
   * @default false
   */
  applied?: boolean;
  /**
   * Whether the toggle is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Click handler
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Button type attribute
   * @default "button"
   */
  type?: "button" | "submit" | "reset";
  /**
   * Custom background color (overrides default styling)
   */
  customBackgroundColor?: string;
  /**
   * Custom text color (overrides default styling)
   */
  defaultContentColor?: string;
  /**
   * Custom border color (overrides default styling)
   */
  customBorderColor?: string;

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * FilterToggle - FauxDS (recreated from Figma MCP)
 *
 * Filter Toggles allow a user to take a single, toggleable action, clicking the
 * Filter Toggle to filter to one, specific theme.
 *
 * Usage tips:
 * - Promote Filter categories that are most common to reduce discovery effort
 * - Use a short, descriptive, single word for the label
 * - Text within the Rest state and Background color within the Applied state can be customized
 * - Ensure color contrast meets AA compliance when customizing
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
const FilterToggle = forwardRef<HTMLButtonElement, FilterToggleProps>(
  (
    {
      label: labelText = "Label",
      Icon,
      "aria-label": accessibilityLabel,
      applied = false,
      disabled = false,
      onClick,
      type = "button",
      customBackgroundColor,
      defaultContentColor: customTextColor,
      customBorderColor,
      className,
    },
    ref
  ) => {
    const ariaLabel = accessibilityLabel || labelText;

    // Determine visual state styles based on Figma design tokens
    const getStateStyles = () => {
      if (disabled) {
        return {
          bg: "bg-(--slate-color-action-surface-disabled)", // #f7f7f7
          border: "border-transparent",
          text: "text-(--slate-color-action-text-disabled)", // #757575
          icon: "text-(--slate-color-action-text-disabled)", // #757575
        };
      }

      // Applied variant (dark background, no border)
      if (applied) {
        return {
          bg: "bg-(--slate-color-action-surface-default) hover:bg-(--slate-color-action-surface-hover) active:bg-(--slate-color-action-surface-active)", // #333333
          border:
            "border-transparent hover:border-transparent active:border-transparent",
          text: "text-(--slate-color-action-text-inverse) hover:text-(--slate-color-action-text-inverse) active:text-(--slate-color-action-text-inverse)", // #ffffff
          icon: "text-(--slate-color-action-text-inverse) hover:text-(--slate-color-action-text-inverse) active:text-(--slate-color-action-text-inverse)", // #ffffff
        };
      }

      // Rest variant (light background, with border)

      return {
        bg: "bg-(--slate-color-action-surface-inverse) hover:bg-(--slate-color-action-surface-inverse) active:bg-(--slate-color-action-surface-inverse)", // #ffffff
        border:
          "border-(--slate-color-action-border-subdued) hover:border-(--slate-color-action-border-hover) active:border-(--slate-color-action-border-active)", // #dfe0e1
        text: "text-(--slate-color-action-text-default) hover:text-(--slate-color-action-text-hover) active:text-(--slate-color-action-text-active)", // #333333
        icon: "text-(--slate-color-action-text-default) hover:text-(--slate-color-action-text-hover) active:text-(--slate-color-action-text-active)", // #333333
      };
    };

    const stateStyles = getStateStyles();

    // Override styles if custom colors are provided
    const finalStyles = {
      bg: customBackgroundColor ? "" : stateStyles.bg,
      border: customBorderColor ? "" : stateStyles.border,
      text: customTextColor ? "" : stateStyles.text,
      icon: customTextColor ? "" : stateStyles.icon,
    };

    // Icon size (18px - increased from 16px)
    const iconSize = 18;

    return (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-pressed={applied}
        className={cn(
          "inline-flex h-[40px] items-center justify-center gap-2 rounded-full border px-4 transition-colors duration-150 ease-in-out focus:outline-hidden focus-visible:ring-2 focus-visible:ring-(--slate-color-action-border-focus-ring) focus-visible:ring-offset-2 disabled:cursor-not-allowed",
          finalStyles.bg,
          finalStyles.border,
          finalStyles.text,
          className
        )}
        style={{
          borderRadius: "var(--slate-radius-component-round)",
          fontFamily: "var(--slate-typography-family-sans-serif)",
          fontSize: "var(--slate-typography-font-size-200)",
          fontWeight: "var(--slate-typography-weight-regular)",
          lineHeight: "var(--slate-typography-line-height-200)",
          letterSpacing: "var(--slate-typography-letter-spacing-200)",
          ...(customBackgroundColor && {
            backgroundColor: customBackgroundColor,
          }),
          ...(customBorderColor && { borderColor: customBorderColor }),
          ...(customTextColor && { color: customTextColor }),
        }}
      >
        {/* Icon */}
        {Icon && (
          <span
            className={cn(
              "flex shrink-0 items-center justify-center",
              finalStyles.icon
            )}
            style={{
              width: iconSize,
              height: iconSize,
              ...(customTextColor && { color: customTextColor }),
            }}
          >
            <Icon />
          </span>
        )}

        {/* Label */}
        <span
          className={finalStyles.text}
          style={customTextColor ? { color: customTextColor } : {}}
        >
          {labelText}
        </span>
      </button>
    );
  }
);

FilterToggle.displayName = "FilterToggle";

export default FilterToggle;
